<template>
  <!-- 运行中：按钮旁气泡 -->
  <Teleport to="body">
    <div
      v-if="bubbleVisible"
      class="smoke-progress-bubble"
      data-smoke-test-btn="1"
      :style="bubbleStyle"
    >
      <div class="smoke-progress-bubble__head">
        <span class="smoke-progress-bubble__dot" :class="statusClass" />
        <strong>{{ headTitle }}</strong>
        <button type="button" class="smoke-progress-bubble__close" title="收起" @click="emit('collapse')">
          ×
        </button>
      </div>
      <div v-if="currentStep" class="smoke-progress-bubble__step">当前：{{ currentStep }}</div>
      <div ref="bubbleBodyRef" class="smoke-progress-bubble__body">
        <div v-for="(line, idx) in recentLines" :key="`${idx}-${line.slice(0, 24)}`" class="smoke-progress-bubble__line">
          {{ plainLine(line) }}
        </div>
        <div v-if="!recentLines.length" class="smoke-progress-bubble__empty">等待进度输出…</div>
      </div>
      <div class="smoke-progress-bubble__foot">
        <span>{{ logs.length }} 条日志</span>
        <button v-if="canExpandReport" type="button" class="smoke-progress-bubble__link" @click="emit('open-report')">
          查看完整报告
        </button>
      </div>
    </div>
  </Teleport>

  <!-- 完成后：放大报告弹框 -->
  <el-dialog
    :model-value="reportVisible"
    class="smoke-report-dialog"
    modal-class="smoke-report-dialog-modal"
    width="720px"
    top="8vh"
    append-to-body
    destroy-on-close
    :close-on-click-modal="false"
    transition="smoke-report-zoom"
    @update:model-value="onReportVisible"
  >
    <template #header>
      <div class="smoke-report-dialog__header">
        <span class="smoke-progress-bubble__dot" :class="statusClass" />
        <span>{{ reportTitle }}</span>
      </div>
    </template>

    <div v-if="suspectBugs.length" class="smoke-report-bugs">
      <h4>疑似 Bug 总结（{{ suspectBugs.length }}）</h4>
      <ol>
        <li v-for="(b, i) in suspectBugs" :key="i">
          <strong>{{ b.step }}</strong>：{{ b.message }}
          <details v-if="b.detail">
            <summary>详情</summary>
            <pre>{{ b.detail }}</pre>
          </details>
        </li>
      </ol>
    </div>
    <div v-else-if="isFinished" class="smoke-report-ok">未记录疑似 Bug。</div>

    <h4 class="smoke-report-log-title">完整过程</h4>
    <div ref="reportBodyRef" class="smoke-report-log">
      <div v-for="(line, idx) in logs" :key="`r-${idx}`" class="smoke-report-log__line">
        {{ plainLine(line) }}
      </div>
    </div>

    <template #footer>
      <el-button @click="emit('update:reportVisible', false)">关闭</el-button>
      <el-button type="primary" @click="copyReport">复制报告</el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { ElButton, ElDialog, ElMessage } from 'element-plus';
import type { SmokeSuspectBug, SmokeTestStatus } from '@/composables/useSmokeTestRunner';

const props = defineProps<{
  status: SmokeTestStatus;
  currentStep: string;
  logs: string[];
  suspectBugs: SmokeSuspectBug[];
  /** 锚点按钮矩形，用于定位气泡 */
  anchorRect: { top: number; left: number; bottom: number; right: number; width: number } | null;
  bubbleVisible: boolean;
  reportVisible: boolean;
}>();

const emit = defineEmits<{
  collapse: [];
  'open-report': [];
  'update:reportVisible': [boolean];
}>();

const bubbleBodyRef = ref<HTMLElement | null>(null);
const reportBodyRef = ref<HTMLElement | null>(null);

const isFinished = computed(() => props.status === 'completed' || props.status === 'failed');
const canExpandReport = computed(() => isFinished.value || props.logs.length > 0);

const statusClass = computed(() => {
  if (props.status === 'running') return 'is-running';
  if (props.status === 'paused') return 'is-paused';
  if (props.status === 'failed') return 'is-failed';
  if (props.status === 'completed') return 'is-done';
  return '';
});

const headTitle = computed(() => {
  switch (props.status) {
    case 'running':
      return '冒烟测试进行中';
    case 'paused':
      return '冒烟测试已暂停';
    case 'completed':
      return '冒烟测试完成';
    case 'failed':
      return '冒烟测试异常结束';
    default:
      return '冒烟测试';
  }
});

const reportTitle = computed(() =>
  props.status === 'failed' ? '冒烟测试报告（异常结束）' : '冒烟测试报告',
);

const recentLines = computed(() => props.logs.slice(-12));

const bubbleStyle = computed(() => {
  const r = props.anchorRect;
  if (!r) {
    return { top: '64px', right: '24px' } as Record<string, string>;
  }
  const width = 360;
  const right = Math.max(12, window.innerWidth - r.right);
  const top = Math.min(r.bottom + 8, window.innerHeight - 280);
  return {
    top: `${top}px`,
    right: `${right}px`,
    width: `${width}px`,
  };
});

function plainLine(line: string): string {
  return line
    .replace(/^###\s*/, '')
    .replace(/\*\*/g, '')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/^---\s*/, '')
    .replace(/^>\s*/, '');
}

function onReportVisible(v: boolean) {
  emit('update:reportVisible', v);
}

async function copyReport() {
  const bugs =
    props.suspectBugs.length === 0
      ? '疑似 Bug：无'
      : `疑似 Bug（${props.suspectBugs.length}）：\n${props.suspectBugs
          .map((b, i) => `${i + 1}. [${b.step}] ${b.message}`)
          .join('\n')}`;
  const text = `${reportTitle.value}\n\n${bugs}\n\n完整过程：\n${props.logs.map(plainLine).join('\n')}`;
  try {
    await navigator.clipboard.writeText(text);
    ElMessage.success('已复制冒烟报告');
  } catch {
    ElMessage.warning('复制失败，请手动选择文本');
  }
}

watch(
  () => props.logs.length,
  async () => {
    await nextTick();
    if (bubbleBodyRef.value) {
      bubbleBodyRef.value.scrollTop = bubbleBodyRef.value.scrollHeight;
    }
    if (reportBodyRef.value) {
      reportBodyRef.value.scrollTop = reportBodyRef.value.scrollHeight;
    }
  },
);
</script>

<style scoped>
.smoke-progress-bubble {
  position: fixed;
  z-index: 4100;
  max-height: min(42vh, 360px);
  display: flex;
  flex-direction: column;
  padding: 10px 12px 8px;
  border-radius: 12px;
  background: linear-gradient(160deg, #fff7f5 0%, #ffffff 55%);
  border: 1px solid rgba(245, 108, 108, 0.45);
  box-shadow:
    0 10px 28px rgba(45, 55, 72, 0.16),
    0 0 0 1px rgba(255, 255, 255, 0.7) inset;
  animation: smoke-bubble-in 0.28s ease-out;
}

@keyframes smoke-bubble-in {
  from {
    opacity: 0;
    transform: translateY(-8px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.smoke-progress-bubble__head {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #303133;
  margin-bottom: 6px;
}

.smoke-progress-bubble__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #c0c4cc;
  flex-shrink: 0;
}

.smoke-progress-bubble__dot.is-running {
  background: #f56c6c;
  box-shadow: 0 0 0 0 rgba(245, 108, 108, 0.55);
  animation: smoke-dot-pulse 1.2s ease-out infinite;
}

.smoke-progress-bubble__dot.is-paused {
  background: #e6a23c;
}

.smoke-progress-bubble__dot.is-done {
  background: #67c23a;
}

.smoke-progress-bubble__dot.is-failed {
  background: #f56c6c;
}

@keyframes smoke-dot-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(245, 108, 108, 0.5);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(245, 108, 108, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(245, 108, 108, 0);
  }
}

.smoke-progress-bubble__close {
  margin-left: auto;
  border: none;
  background: transparent;
  font-size: 16px;
  line-height: 1;
  color: #909399;
  cursor: pointer;
}

.smoke-progress-bubble__step {
  font-size: 12px;
  color: #e6a23c;
  margin-bottom: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.smoke-progress-bubble__body {
  flex: 1;
  min-height: 120px;
  max-height: 220px;
  overflow: auto;
  padding: 8px;
  border-radius: 8px;
  background: rgba(48, 49, 51, 0.04);
  font-size: 12px;
  line-height: 1.45;
  color: #606266;
}

.smoke-progress-bubble__line {
  margin-bottom: 4px;
  word-break: break-word;
  white-space: pre-wrap;
}

.smoke-progress-bubble__empty {
  color: #a8abb2;
}

.smoke-progress-bubble__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 6px;
  font-size: 11px;
  color: #909399;
}

.smoke-progress-bubble__link {
  border: none;
  background: none;
  color: #f56c6c;
  cursor: pointer;
  font-size: 11px;
  font-weight: 600;
  padding: 0;
}

.smoke-report-dialog__header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
}

.smoke-report-bugs {
  margin-bottom: 14px;
  padding: 12px 14px;
  border-radius: 8px;
  background: #fef0f0;
  color: #613434;
  font-size: 13px;
}

.smoke-report-bugs h4,
.smoke-report-log-title {
  margin: 0 0 8px;
  font-size: 14px;
}

.smoke-report-bugs ol {
  margin: 0;
  padding-left: 18px;
}

.smoke-report-bugs details {
  margin-top: 4px;
}

.smoke-report-bugs pre {
  margin: 6px 0 0;
  padding: 8px;
  background: #fff;
  border-radius: 6px;
  font-size: 11px;
  overflow: auto;
  max-height: 160px;
}

.smoke-report-ok {
  margin-bottom: 12px;
  color: #67c23a;
  font-size: 13px;
}

.smoke-report-log {
  max-height: 46vh;
  overflow: auto;
  padding: 12px;
  border-radius: 8px;
  background: #1e1e1e;
  color: #e8e8e8;
  font-size: 12px;
  line-height: 1.5;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}

.smoke-report-log__line {
  white-space: pre-wrap;
  word-break: break-word;
  margin-bottom: 2px;
}
</style>

<style>
/* 气泡放大为报告弹框的过渡（挂到 body，需非 scoped） */
.smoke-report-zoom-enter-active,
.smoke-report-zoom-leave-active {
  transition:
    opacity 0.28s ease,
    transform 0.32s cubic-bezier(0.22, 1, 0.36, 1);
}

.smoke-report-zoom-enter-from,
.smoke-report-zoom-leave-to {
  opacity: 0;
  transform: scale(0.72) translateY(-24px);
}

.smoke-report-zoom-enter-to,
.smoke-report-zoom-leave-from {
  opacity: 1;
  transform: scale(1) translateY(0);
}

.smoke-report-dialog.el-dialog {
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 18px 48px rgba(45, 55, 72, 0.22);
}
</style>
